import { useState } from "react";
import { X } from "lucide-react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { IconButton } from "@/components/ui/icon-button";
import { DatePicker } from "@/components/ui/date-picker";
import { InfoHint } from "@/components/ui/info-hint";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { milestoneDateBounds } from "@/features/field-pilot/schedule-format";
import { StatusDot, StatusOptionLabel } from "@/features/field-pilot/status-dot";

export interface MilestoneEditorStatus {
  _id: Id<"fieldPilotStatuses">;
  name: string;
  colorHex: string;
}

/** One pipeline status paired with its rank (index) so callers can reason about ordering. */
interface RankedStatus {
  status: MilestoneEditorStatus;
  rank: number;
}

// Shared column widths so the status dropdown / labels and the date pickers line up across every
// row (existing milestones + the add row). Heights come from the matching `size="sm"` on both the
// Select and DatePicker so the two controls are always the same size.
const STATUS_COL = "w-[168px]";
const DATE_COL = "w-[150px]";

/** A single already-dated milestone: status label, its due-date picker, and a remove control. */
function MilestoneRow({
  status,
  rank,
  dueDateKey,
  datedForBounds,
  disabled,
  onSetMilestone,
  onRemoveMilestone,
}: {
  status: MilestoneEditorStatus;
  rank: number;
  dueDateKey: string;
  datedForBounds: { rank: number; dueDateKey: string | null }[];
  disabled?: boolean;
  onSetMilestone: (statusId: Id<"fieldPilotStatuses">, dueDateKey: string) => void;
  onRemoveMilestone: (statusId: Id<"fieldPilotStatuses">) => void;
}) {
  const bounds = milestoneDateBounds(datedForBounds, rank);
  return (
    <div className="flex items-center gap-2">
      <span className={`flex ${STATUS_COL} min-w-0 items-center gap-2 text-sm font-medium`}>
        <StatusDot colorHex={status.colorHex} />
        <span className="truncate">{status.name}</span>
      </span>
      <DatePicker
        className={DATE_COL}
        size="sm"
        value={dueDateKey}
        disabled={disabled}
        min={bounds.min}
        max={bounds.max}
        onChange={(value) => {
          if (value) {
            onSetMilestone(status._id, value);
          } else {
            onRemoveMilestone(status._id);
          }
        }}
        aria-label={`Due date for ${status.name}`}
      />
      <IconButton
        label={`Remove ${status.name} milestone`}
        icon={X}
        iconClassName="size-3.5"
        disabled={disabled}
        onClick={() => onRemoveMilestone(status._id)}
      />
    </div>
  );
}

/** Status select (pre-selected to the next eligible status) + due-date picker for adding a milestone. */
function AddMilestoneRow({
  addable,
  datedForBounds,
  disabled,
  onSetMilestone,
}: {
  addable: RankedStatus[];
  datedForBounds: { rank: number; dueDateKey: string | null }[];
  disabled?: boolean;
  onSetMilestone: (statusId: Id<"fieldPilotStatuses">, dueDateKey: string) => void;
}) {
  const [addStatusId, setAddStatusId] = useState("");

  // Default the add row to the next un-dated status so the common case is a single date pick.
  const effectiveAddStatusId =
    addStatusId && addable.some((entry) => String(entry.status._id) === addStatusId)
      ? addStatusId
      : String(addable[0]?.status._id ?? "");
  const addRank = effectiveAddStatusId
    ? addable.find((entry) => String(entry.status._id) === effectiveAddStatusId)?.rank
    : undefined;
  const addBounds = addRank === undefined ? {} : milestoneDateBounds(datedForBounds, addRank);

  return (
    <div className="mt-3 flex items-center gap-2 border-t pt-3">
      <Select value={effectiveAddStatusId} onValueChange={setAddStatusId} disabled={disabled}>
        <SelectTrigger size="sm" className={STATUS_COL} aria-label="Milestone status">
          <SelectValue placeholder="Add milestone…" />
        </SelectTrigger>
        <SelectContent className="z-[200]">
          {addable.map((entry) => (
            <SelectItem key={String(entry.status._id)} value={String(entry.status._id)}>
              <StatusOptionLabel colorHex={entry.status.colorHex} name={entry.status.name} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DatePicker
        className={DATE_COL}
        size="sm"
        value=""
        disabled={disabled || !effectiveAddStatusId}
        min={addBounds.min}
        max={addBounds.max}
        placeholder="Set due date"
        onChange={(value) => {
          if (value && effectiveAddStatusId) {
            onSetMilestone(effectiveAddStatusId as Id<"fieldPilotStatuses">, value);
            setAddStatusId("");
          }
        }}
        aria-label="New milestone due date"
      />
      {/* Spacer keeps the date column aligned with the remove-button column above. */}
      <span className="size-8 shrink-0" aria-hidden="true" />
    </div>
  );
}

/**
 * Start date + milestone due-date editor for a sub-task. Mirrors the schedule drill-down: a start
 * picker on top, then only the statuses that already carry a due date, followed by an "Add
 * milestone" row (pre-selected to the next eligible status) for the remaining statuses. Setting a
 * date upserts that status's milestone; clearing it removes the milestone. Used by the sub-task
 * dialog for both newly created and existing sub-tasks.
 */
export function MilestoneEditor({
  statuses,
  dueDateByStatusId,
  startDateKey,
  onSetStart,
  onSetMilestone,
  onRemoveMilestone,
  disabled,
}: {
  statuses: MilestoneEditorStatus[];
  dueDateByStatusId: Map<string, string>;
  /** The sub-task's explicit start override; empty string when it inherits the parent task start. */
  startDateKey: string;
  onSetStart: (startDateKey: string | null) => void;
  onSetMilestone: (statusId: Id<"fieldPilotStatuses">, dueDateKey: string) => void;
  onRemoveMilestone: (statusId: Id<"fieldPilotStatuses">) => void;
  disabled?: boolean;
}) {
  // rank = index in the pipeline; the starting stage (rank 0) is never a milestone.
  const eligible: RankedStatus[] = statuses
    .map((status, index) => ({ status, rank: index }))
    .filter((entry) => entry.rank > 0);
  const setMilestones = eligible.filter((entry) =>
    dueDateByStatusId.has(String(entry.status._id))
  );
  const addable = eligible.filter((entry) => !dueDateByStatusId.has(String(entry.status._id)));

  // Every dated milestone, used to constrain pickers so dates can't be set out of pipeline order
  // (the backend rejects out-of-order dates; these bounds prevent it up front).
  const datedForBounds = eligible.map((entry) => ({
    rank: entry.rank,
    dueDateKey: dueDateByStatusId.get(String(entry.status._id)) ?? null,
  }));
  // The latest dated milestone caps the start so it can't fall after the final milestone.
  const latestMilestoneDateKey = datedForBounds.reduce<string | undefined>(
    (latest, milestone) =>
      milestone.dueDateKey && (latest === undefined || milestone.dueDateKey > latest)
        ? milestone.dueDateKey
        : latest,
    undefined
  );

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Start date
        </Label>
        <DatePicker
          className={STATUS_COL}
          size="sm"
          value={startDateKey}
          max={latestMilestoneDateKey}
          disabled={disabled}
          placeholder="Inherited"
          onChange={(value) => onSetStart(value ? value : null)}
          aria-label="Sub-task start date"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Milestone due dates
          </Label>
          <InfoHint label="About milestone due dates">
            Only statuses with a target date are shown. Clear a date to drop that milestone, or add
            one below.
          </InfoHint>
        </div>

        {setMilestones.length > 0 ? (
          <div className="space-y-1.5">
            {setMilestones.map((entry) => (
              <MilestoneRow
                key={entry.status._id}
                status={entry.status}
                rank={entry.rank}
                dueDateKey={dueDateByStatusId.get(String(entry.status._id)) ?? ""}
                datedForBounds={datedForBounds}
                disabled={disabled}
                onSetMilestone={onSetMilestone}
                onRemoveMilestone={onRemoveMilestone}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No milestone dates yet.</p>
        )}

        {addable.length > 0 ? (
          <AddMilestoneRow
            addable={addable}
            datedForBounds={datedForBounds}
            disabled={disabled}
            onSetMilestone={onSetMilestone}
          />
        ) : null}
      </div>
    </div>
  );
}
